import { useLocalStorage } from "usehooks-ts";
import { PerformanceReviewScenarioType } from "./usePerformanceReviewData";

export type PerformanceReviewThresholds = {
  noReview12mDays: number;         // trigger NO_REVIEW_12M (default 365)
  noReview12mHighDays: number;     // escalate to high (default 545)
  longTenureDays: number;          // trigger LONG_TENURE_NO_REVIEW (default 548)
  probationMinDays: number;        // start of FIRST_REVIEW_UPCOMING window (default 75)
  probationMaxDays: number;        // end of FIRST_REVIEW_UPCOMING window (default 120)
};

export type PerformanceReviewConfig = {
  enabledSignals: Record<PerformanceReviewScenarioType, boolean>;
  thresholds: PerformanceReviewThresholds;
};

export const DEFAULT_PR_CONFIG: PerformanceReviewConfig = {
  enabledSignals: {
    NO_REVIEW_12M: true,
    NEW_MANAGER_NO_REVIEW: true,
    LONG_TENURE_NO_REVIEW: true,
    ROLE_CHANGE_REQUESTED: true,
    FIRST_REVIEW_UPCOMING: true
  },
  thresholds: {
    noReview12mDays: 365,
    noReview12mHighDays: 545,
    longTenureDays: 548,
    probationMinDays: 75,
    probationMaxDays: 120
  }
};

const STORAGE_KEY = "perf-review:config";

export const usePerformanceReviewConfig = () => {
  const [config, setConfig] = useLocalStorage<PerformanceReviewConfig>(
    STORAGE_KEY,
    DEFAULT_PR_CONFIG
  );

  const setSignalEnabled = (type: PerformanceReviewScenarioType, enabled: boolean) => {
    setConfig(prev => ({
      ...prev,
      enabledSignals: { ...prev.enabledSignals, [type]: enabled }
    }));
  };

  const setThreshold = (key: keyof PerformanceReviewThresholds, value: number) => {
    setConfig(prev => ({
      ...prev,
      thresholds: { ...prev.thresholds, [key]: value }
    }));
  };

  const resetToDefaults = () => setConfig(DEFAULT_PR_CONFIG);

  return { config, setSignalEnabled, setThreshold, resetToDefaults };
};
